import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  AccountBalanceWalletOutlined,
  ArrowBack,
  AssignmentOutlined,
  AttachFileOutlined,
  DescriptionOutlined,
  EqualizerOutlined,
  ExpandLess,
  ExpandMore,
  HomeOutlined,
  KeyboardArrowRight
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useLeaseContract, useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractProtocols } from 'services/protocols/protocols.hooks'
import { useLanguage } from 'services/i18n/i18n.hooks'
import { useUsersProfile } from 'services/profile/profile.hooks'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import MobileBottomFooter from 'components/MobileBottomFooter'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import {
  ProtocolMetersReadings,
  ProtocolRooms,
  ProtocolFurniture,
  ProtocolItems,
  ProtocolKeys
} from 'components/Protocols'
import theme from 'styles/theme'

type ProtocolType = 'Acceptance' | 'Delivery'

const ProtocolSection: FC<{
  title: string
  defaultOpen?: boolean
  children: ReactNode
}> = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setOpen] = useState(defaultOpen)

  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      <ButtonBase
        onClick={() => setOpen((state) => !state)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '18px 24px',
          borderBottom: isOpen ? 'none' : `1px solid ${theme.r4r.greyLight}`
        }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.ink }}>
          {title}
        </Typography>
        {isOpen ? (
          <ExpandLess sx={{ fontSize: '20px', color: theme.r4r.grey }} />
        ) : (
          <ExpandMore sx={{ fontSize: '20px', color: theme.r4r.grey }} />
        )}
      </ButtonBase>
      <Collapse in={isOpen}>
        <Box sx={{ px: '24px', pt: '20px', pb: '21px', overflowX: 'auto' }}>{children}</Box>
      </Collapse>
    </Box>
  )
}

// Handover protocol ("Panel Klienta 3.0": Figma "Umowy — Protokół Z-O").
const AdditionalServicesProtocol: FC = () => {
  const [activeProtocol, setActiveProtocol] = useState<ProtocolType>('Acceptance')
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { language } = useLanguage()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { id } = useParams()
  const { activeLeaseContractId } = useActiveLeaseContractId()
  const { protocolsList, acceptProtocol } = useLeaseContractProtocols(activeLeaseContractId)
  const { getActiveContract } = useLeaseContractsList()
  const { leaseContract } = useLeaseContract(activeLeaseContractId)
  const isOperationsFrozen = getActiveContract(activeLeaseContractId)?.isOperationsFrozen
  const { data = { firstName: '', lastName: '' } } = useUsersProfile()

  useEffect(() => {
    if (!protocolsList.length) {
      return
    }

    const hasActive = protocolsList.some(({ protocolType }) => protocolType === activeProtocol)
    if (!hasActive) {
      setActiveProtocol(protocolsList[0].protocolType as ProtocolType)
    }
  }, [activeProtocol, protocolsList])

  const protocol = useMemo(
    () => protocolsList.find(({ protocolType }) => protocolType === activeProtocol),
    [protocolsList, activeProtocol]
  )

  const handleAcceptProtocol = async () => {
    try {
      await acceptProtocol.mutateAsync(protocol?.id)
      enqueueSnackbar(
        formatMessage({
          id: 'additionalServicesProtocolAccept.form.success',
          defaultMessage: 'Protokuł został złożny poprawnie.'
        }),
        { variant: 'success' }
      )
    } catch (error) {
      enqueueSnackbar(
        formatMessage({
          id: 'additionalServicesProtocolAccept.form.error',
          defaultMessage: 'Nie można złożyć protokołu.'
        }),
        { variant: 'error' }
      )
    }
  }

  const navItems = [
    {
      key: 'preview',
      icon: <DescriptionOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({ id: 'additionalServicesMedia.preview', defaultMessage: 'Podgląd umowy' }),
      onClick: () => window.open(leaseContract?.leaseContractFile, '_blank')
    },
    {
      key: 'annexes',
      icon: <AttachFileOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({ id: 'additionalServicesMedia.annexes', defaultMessage: 'Aneksy' }),
      onClick: () => navigate(`/additional-services/${id}/annexes`)
    },
    {
      key: 'schedule',
      icon: <AccountBalanceWalletOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({
        id: 'additionalServicesPaymentSchedule.schedule',
        defaultMessage: 'Harmonogram płatności'
      }),
      onClick: () => navigate(`/additional-services/${id}/payment-schedule`)
    },
    {
      key: 'information',
      icon: <HomeOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({
        id: 'additionalServicesInformation.information',
        defaultMessage: 'Podstawowe informacje'
      }),
      onClick: () => navigate(`/additional-services/${id}/information`)
    },
    {
      key: 'media',
      icon: <EqualizerOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({ id: 'additionalServicesMedia.media', defaultMessage: 'Media' }),
      onClick: () => navigate(`/additional-services/${id}/media`)
    },
    {
      key: 'protocol',
      icon: <AssignmentOutlined sx={{ fontSize: '20px' }} />,
      label: formatMessage({
        id: 'acceptanceProtocolListItem.protocol',
        defaultMessage: 'Protokół Z-O'
      }),
      onClick: undefined,
      active: true
    }
  ]

  const tabs: { value: ProtocolType; label: string }[] = [
    {
      value: 'Acceptance',
      label: formatMessage({
        id: 'additionalServicesProtocol.deliveryTab',
        defaultMessage: 'Zdawczy'
      })
    },
    {
      value: 'Delivery',
      label: formatMessage({
        id: 'additionalServicesProtocol.acceptanceTab',
        defaultMessage: 'Odbiorczy'
      })
    }
  ]

  const tabBar = (
    <Box sx={{ display: 'flex', maxWidth: '320px' }}>
      {tabs.map(({ value, label }) => {
        const isSelected = value === activeProtocol
        return (
          <ButtonBase
            key={value}
            onClick={() => setActiveProtocol(value)}
            sx={{
              flex: 1,
              padding: '11px 20px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: muiTheme.typography.fontFamily,
              backgroundColor: isSelected ? theme.r4r.navy : '#fff',
              color: isSelected ? '#fff' : theme.r4r.grey,
              border: `1px solid ${isSelected ? theme.r4r.navy : theme.r4r.greyLight}`,
              ...(value === 'Delivery' && !isSelected
                ? { borderLeft: 'none' }
                : {})
            }}>
            {label}
          </ButtonBase>
        )
      })}
    </Box>
  )

  const protocolBody = protocol ? (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <ProtocolSection
          defaultOpen
          title={formatMessage({
            id: 'acceptanceProtocolListItem.mediaMeter',
            defaultMessage: 'Liczniki'
          })}>
          <ProtocolMetersReadings readings={protocol.metersReadings} />
        </ProtocolSection>
        <ProtocolSection
          title={formatMessage({
            id: 'acceptanceProtocolListItem.rooms',
            defaultMessage: 'Pokoje'
          })}>
          <ProtocolRooms
            rooms={protocol.rooms}
            personComments={protocol.personComments}
            questionsAnswers={protocol.questionsAnswers}
            protocolId={protocol.id}
          />
        </ProtocolSection>
        <ProtocolSection
          title={formatMessage({
            id: 'acceptanceProtocolListItem.furniture',
            defaultMessage: 'Meble'
          })}>
          <ProtocolFurniture furniture={protocol.furniture} />
        </ProtocolSection>
        <ProtocolSection
          title={formatMessage({
            id: 'acceptanceProtocolListItem.items',
            defaultMessage: 'Przedmioty'
          })}>
          <ProtocolItems items={protocol.items} />
        </ProtocolSection>
        <ProtocolSection
          title={formatMessage({
            id: 'acceptanceProtocolListItem.keys',
            defaultMessage: 'Klucze'
          })}>
          <ProtocolKeys keys={protocol.keys} />
        </ProtocolSection>
        <ProtocolSection
          title={formatMessage({
            id: 'additionalServicesProtocol.generalComment',
            defaultMessage: 'Ogólny komentarz'
          })}>
          <Typography sx={{ fontSize: '14px', color: theme.r4r.ink }}>
            {protocol.generalComment ||
              formatMessage({ id: 'common.noData', defaultMessage: 'Brak danych' })}
          </Typography>
        </ProtocolSection>
      </Box>
      {protocol.status === 'Handed over' && !isOperationsFrozen && (
        <MobileBottomFooter>
          <Box sx={{ mb: '20px' }}>
            {language === 'pl' ? (
              <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
                Ja, niżej podpisana/podpisany {data?.firstName} {data?.lastName}, niniejszym
                oświadczam, że przyjmuję do wiadomości, że podpisanie niniejszego protokołu lub
                zwrot Przedmiotu Umowy przed upływem okresu przewidzianego w Umowie, nie jest
                równoznaczny z rozwiązaniem Umowy.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
                I, the undersigned {data?.firstName} {data?.lastName}, hereby declare that I
                acknowledge that signing this protocol or returning the Subject of the Agreement
                before the expiry of the period provided for in the Agreement is not equivalent to
                termination of the Agreement.
              </Typography>
            )}
          </Box>
          <LoadingButton
            variant='contained'
            fullWidth
            loading={acceptProtocol.isLoading}
            disabled={acceptProtocol.isLoading}
            onClick={handleAcceptProtocol}
            sx={{
              backgroundColor: theme.r4r.navy,
              color: '#fff',
              fontWeight: 700,
              borderRadius: 0,
              boxShadow: 'none',
              ':hover': { backgroundColor: theme.r4r.ink, boxShadow: 'none' }
            }}>
            {formatMessage({
              id: 'additionalServicesProtocolSpaces.form.accept',
              defaultMessage: 'Zaakceptuj protokół'
            })}
          </LoadingButton>
        </MobileBottomFooter>
      )}
    </>
  ) : (
    <HelpdeskEmptyState
      Icon={AssignmentOutlined}
      title={formatMessage({
        id: 'common.noData',
        defaultMessage: 'Brak danych'
      })}
      action={
        <Button
          onClick={() => navigate(`/additional-services/${activeLeaseContractId}`)}
          sx={{ color: theme.r4r.navy, fontWeight: 700 }}>
          {formatMessage({
            id: 'common.goBack',
            defaultMessage: 'Wróć'
          })}
        </Button>
      }
    />
  )

  if (isDesktop) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 'calc(100vh - 92px)',
          mx: '-36px',
          mt: '-28px',
          mb: '-64px'
        }}>
        <Box
          sx={{
            width: '360px',
            flexShrink: 0,
            backgroundColor: '#fff',
            borderRight: `1px solid ${theme.r4r.greyLight}`,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', pt: '24px', px: '28px', pb: '20px' }}>
            <IconButton
              onClick={() => navigate(`/additional-services/${id}`)}
              sx={{ width: '32px', height: '32px', color: theme.r4r.ink, borderRadius: 0 }}>
              <ArrowBack sx={{ fontSize: '22px' }} />
            </IconButton>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, lineHeight: '24px', color: theme.r4r.ink }}>
              {formatMessage({
                id: 'acceptanceProtocolListItem.protocol',
                defaultMessage: 'Protokół Z-O'
              })}
            </Typography>
          </Box>
          <Box>
            {navItems.map((item) => (
              <ButtonBase
                key={item.key}
                onClick={item.onClick}
                disabled={!item.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  width: '100%',
                  textAlign: 'left',
                  padding: '16px 28px',
                  borderBottom: `1px solid ${theme.r4r.greyLight}`,
                  backgroundColor: item.active ? alpha(theme.r4r.navy, 0.04) : '#fff',
                  '&.Mui-disabled': { opacity: 1 }
                }}>
                <Box sx={{ color: item.active ? theme.r4r.navy : theme.r4r.grey, display: 'flex' }}>
                  {item.icon}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '14px',
                    fontWeight: item.active ? 700 : 400,
                    color: item.active ? theme.r4r.navy : theme.r4r.ink
                  }}>
                  {item.label}
                </Typography>
                <KeyboardArrowRight
                  sx={{ fontSize: '16px', color: item.active ? theme.r4r.navy : theme.r4r.grey }}
                />
              </ButtonBase>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: theme.r4r.greyBg,
            padding: '28px 36px',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px'
          }}>
          {tabBar}
          {protocolBody}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'acceptanceProtocolListItem.protocol',
          defaultMessage: 'Protokół Z-O'
        })}
        navigateTo={`/additional-services/${id}`}
      />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)' sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {tabBar}
          {protocolBody}
        </Box>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesProtocol
